/**
 * ストリートビュー3Dパノラマ重畳の方位計算 (docs/25 §11、Increment 3、pure関数のみ)。
 *
 * 外部参照実装 (ECW-W Flask版) との重要な差分: Flask版は `image_index * 90` のみで
 * `base_heading` を加算しない簡略化 (base_heading が0でない地点では方角がズレるバグの可能性)。
 * 本実装は docs/25 §11 の正式な式のとおり、必ず `baseHeading` を加算する。
 */

/** box2d = [ymin, xmin, ymax, xmax] (0〜1000スケール、`StreetViewPin.box2d` と同じ形)。 */
export type Box2d = [number, number, number, number];

/**
 * 検出要素の水平方位 (度、0〜360) を計算する。
 * `baseHeading`(撮影基準方角) + `imageIndex*90`(4方向オフセット) + box2d水平中心の
 * fov内での角度換算、を合成する。
 */
export function computeMarkerAzimuth(
  baseHeading: number,
  imageIndex: 0 | 1 | 2 | 3,
  box2d: Box2d,
  fov: number,
): number {
  const [, xmin, , xmax] = box2d;
  const centerX = (xmin + xmax) / 2 / 1000; // 0..1
  const azimuth = baseHeading + imageIndex * 90 + (centerX - 0.5) * fov;
  return ((azimuth % 360) + 360) % 360;
}

/**
 * box2d の垂直方向の中心位置 (0..1、小さいほど画像上方) を返す。tooltip 内の付随情報
 * (画像内の相対位置) の表示に用いる。
 */
export function computeVerticalCenterRatio(box2d: Box2d): number {
  const [ymin, , ymax] = box2d;
  return (ymin + ymax) / 2 / 1000;
}

/**
 * 静止画の `size` ("640x480" 形式) から縦横比を解釈し、**垂直**画角 (度) を返す。
 *
 * Street View Static API の `fov` は **水平**画角であり、既定の取得サイズは 640x480 (4:3)
 * なので垂直画角は水平と一致しない (fov=90 → vFOV≈73.74°)。透視投影の関係
 * `tan(vFov/2) = tan(hFov/2) × (height/width)` で換算する (2026-07-16 修正、それ以前は
 * 水平 fov をそのまま垂直に適用しており、画像上端の要素で最大約8°の仰角誤差があった)。
 * size が解釈できない場合は正方形とみなし水平 fov をそのまま返す。
 */
export function computeVerticalFovDeg(hFovDeg: number, size: string | undefined): number {
  const m = /^(\d+)x(\d+)$/.exec((size ?? '').trim());
  if (!m) return hFovDeg;
  const width = Number(m[1]);
  const height = Number(m[2]);
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) {
    return hFovDeg;
  }
  const halfH = (hFovDeg / 2) * (Math.PI / 180);
  const halfV = Math.atan(Math.tan(halfH) * (height / width));
  return ((halfV * 180) / Math.PI) * 2;
}

/**
 * 検出要素の仰角 (度、水平=0、上方向が正) を計算する。撮影時 pitch=0 で撮った静止画の
 * box2d 垂直中心を **垂直**画角 (`computeVerticalFovDeg` で換算) 内の角度に換算する。
 * ネイティブ google.maps.Marker は緯度経度 (=地上高さ0m) にしか配置できず高さを表現
 * できないため、この仰角は Marker ではなくカスタムARオーバーレイ (現在のパノラマ視点を
 * 基準にした透視投影、`projectToScreen`) でのみ用いる (2026-07-14)。
 *
 * 第2引数は **垂直**画角である点に注意 (水平 fov を渡すと上下に間延びする)。
 */
export function computeMarkerElevation(box2d: Box2d, vFovDeg: number): number {
  const [ymin, , ymax] = box2d;
  const centerY = (ymin + ymax) / 2 / 1000; // 0..1, 0が画像上端
  return (0.5 - centerY) * vFovDeg;
}

function normalizeAngleSigned(deg: number): number {
  let d = deg % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
}

export interface ScreenProjection {
  x: number;
  y: number;
  /** 現在のパノラマ視野角内にあるか (画角のわずかに手前で足切りし、端で歪みすぎる描画を避ける)。 */
  visible: boolean;
}

/**
 * 対象の方位・仰角と現在のパノラマ視点 (heading/pitch/水平画角) から、コンテナ内の
 * スクリーン座標を透視投影 (tanベース、広角でも歪みを考慮) で計算する (custom ARオーバーレイ、
 * 2026-07-14)。パノラマをドラッグ/ズームするたびに呼び直され、対象が視野外に出ると
 * `visible: false` になる。
 */
export function projectToScreen(opts: {
  targetAzimuthDeg: number;
  targetElevationDeg: number;
  cameraHeadingDeg: number;
  cameraPitchDeg: number;
  hFovDeg: number;
  containerWidth: number;
  containerHeight: number;
}): ScreenProjection {
  const {
    targetAzimuthDeg,
    targetElevationDeg,
    cameraHeadingDeg,
    cameraPitchDeg,
    hFovDeg,
    containerWidth,
    containerHeight,
  } = opts;
  const relYawDeg = normalizeAngleSigned(targetAzimuthDeg - cameraHeadingDeg);
  const relPitchDeg = targetElevationDeg - cameraPitchDeg;

  const hFovRad = (hFovDeg * Math.PI) / 180;
  const halfH = hFovRad / 2;
  // 縦画角は横画角とコンテナのアスペクト比から算出 (同一焦点距離を仮定したtan補正)。
  const halfV = Math.atan(Math.tan(halfH) * (containerHeight / containerWidth));

  const relYawRad = (relYawDeg * Math.PI) / 180;
  const relPitchRad = (relPitchDeg * Math.PI) / 180;

  const visible = Math.abs(relYawRad) < halfH * 0.98 && Math.abs(relPitchRad) < halfV * 0.98;

  const x = containerWidth / 2 + (containerWidth / 2) * (Math.tan(relYawRad) / Math.tan(halfH));
  const y = containerHeight / 2 - (containerHeight / 2) * (Math.tan(relPitchRad) / Math.tan(halfV));

  return { x, y, visible };
}

/**
 * Street View JS の zoom レベルから水平画角(度)を近似する。Google 非公式だがコミュニティで
 * 広く使われる近似式 (zoom=0 で撮影時の既定fov 90° 相当、1段階につき半分)。近似案として
 * 十分な精度 (docs/25 §13-4)。
 */
export function zoomToHFovDeg(zoom: number): number {
  return 180 / Math.pow(2, zoom + 1);
}
